import config from '#config';

interface ZendeskSupportedWindow extends Window {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type ZendeskChatInitOptions = {
  openChatOnMount: boolean;
  showContactFormOnMount: boolean;
};

const DEFAULT_OPTIONS: ZendeskChatInitOptions = {
  openChatOnMount: false,
  showContactFormOnMount: false,
};

const init = (options: ZendeskChatInitOptions = DEFAULT_OPTIONS): void => {
  const globalWindowObject = (<unknown>window) as ZendeskSupportedWindow;
  const script = document.createElement('script');
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${config.ZENDESK_KEY}`;
  script.async = true;
  script.id = 'ze-snippet';
  script.onload = zopimCall;

  if (!document.getElementById('ze-snippet')) {
    document.body.appendChild(script);
  } else {
    zopimCall();
  }

  let onlineDepartment = '';

  function zopimCall() {
    const loadZopimInterval = setInterval(zopimFunctionDetected, 500);

    function zopimFunctionDetected() {
      if (globalWindowObject.$zopim !== undefined) {
        clearInterval(loadZopimInterval);
        initChat();
        // filter if initChat function will not work
        globalWindowObject.$zopim.livechat?.departments.filter(config.ZENDESK_DEPARTMENT);
      }
    }

    return null;
  }

  function initChat() {
    if (options.openChatOnMount) {
      globalWindowObject.zE(function () {
        globalWindowObject.zE.activate();

        globalWindowObject.zE('webWidget', 'updateSettings', {
          webWidget: {
            helpCenter: {
              suppress: options.showContactFormOnMount,
            },
            contactForm: {
              suppress: false,
            },
          },
        });
      });
    }

    globalWindowObject.zE('webWidget:on', 'chat:connected', function () {
      const existingDepartments = globalWindowObject.zE('webWidget:get', 'chat:departments');
      const currentDepartment = existingDepartments.filter(
        ({ name }: { name: string }) => name === config.ZENDESK_DEPARTMENT,
      );

      if (currentDepartment.length > 0) {
        onlineDepartment = existingDepartments
          .filter(
            ({ name, status }: { name: string; status: string }) =>
              config.ZENDESK_DEPARTMENT === name && ['away', 'online'].includes(status),
          )
          .map((elem: { name: string }) => elem.name);
      }

      globalWindowObject.zE('webWidget', 'updateSettings', {
        webWidget: {
          helpCenter: {
            suppress: options.openChatOnMount,
          },
          chat: {
            ...(onlineDepartment.length === 0
              ? { suppress: true }
              : {
                  departments: {
                    enabled: onlineDepartment,
                    select: onlineDepartment[0],
                  },
                  offlineForm: {
                    greeting: {
                      '*': "We aren't online right now, please leave a message",
                    },
                  },
                }),
          },
        },
      });
    });
  }
};

function show(): void {
  const globalWindowObject = (<unknown>window) as ZendeskSupportedWindow;
  if (globalWindowObject.$zopim !== undefined) {
    globalWindowObject.zE('webWidget', 'show');
  }
}

function hide(): void {
  const globalWindowObject = (<unknown>window) as ZendeskSupportedWindow;
  if (globalWindowObject.$zopim !== undefined) {
    globalWindowObject.zE('webWidget', 'hide');
  }
}

function openContactForm(): void {
  const globalWindowObject = (<unknown>window) as ZendeskSupportedWindow;
  if (globalWindowObject.$zopim !== undefined) {
    globalWindowObject.zE(function () {
      globalWindowObject.zE.activate();
      globalWindowObject.zE('webWidget', 'updateSettings', {
        webWidget: {
          helpCenter: {
            suppress: true,
          },
        },
      });
    });
  }
}

export default {
  init,
  show,
  hide,
  openContactForm,
};
